import { useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import {
  buildAvatarStyle,
  buildChannelDotStyle,
  getInitials,
  pickToneBg,
} from './SuperAvatar.styles';
import type { SuperAvatarProps } from './SuperAvatar.types';

export interface SuperAvatarViewModel {
  initials: string;
  showImage: boolean;
  style: CSSProperties;
  channelDotStyle: CSSProperties | null;
  handleImageError: () => void;
}

const channelToVar: Record<string, string> = {
  whatsapp: 'var(--color-whatsapp)',
  instagram: 'var(--color-instagram)',
  facebook: 'var(--color-facebook)',
  telegram: 'var(--color-telegram)',
  webchat: 'var(--color-webchat)',
  email: 'var(--color-email)',
};

export function useSuperAvatar(props: SuperAvatarProps): SuperAvatarViewModel {
  const { name, src, size = 'md', shape = 'circle', channel, toneSeed, style: incoming } = props;
  const [imgFailed, setImgFailed] = useState(false);

  const initials = useMemo(() => getInitials(name), [name]);
  const tone = useMemo(() => pickToneBg(toneSeed ?? name), [toneSeed, name]);

  const style = useMemo<CSSProperties>(
    () => ({ ...buildAvatarStyle({ size, shape, tone }), ...incoming }),
    [size, shape, tone, incoming],
  );

  const channelDotStyle = useMemo(
    () =>
      channel && channelToVar[channel] ? buildChannelDotStyle(size, channelToVar[channel]!) : null,
    [channel, size],
  );

  return {
    initials,
    showImage: Boolean(src) && !imgFailed,
    style,
    channelDotStyle,
    handleImageError: () => setImgFailed(true),
  };
}
